import { sectionRenderer } from '@theme/section-renderer';
import { prefersReducedMotion } from '@theme/utilities';

class BlogGrid extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Blog grid requires a section-id attribute');
    return id;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.blog-grid__tag');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    event.preventDefault();
    link.blur();

    const url = link.href;
    history.pushState({ blogTagFilter: true }, '', url);
    this.#filterByURL(url);
  };

  #handlePopState = () => {
    this.#filterByURL(window.location.href, { scroll: false });
  };

  /**
   * @param {string} url
   * @param {{ scroll?: boolean }} [options]
   */
  async #filterByURL(url, { scroll = true } = {}) {
    if (this.#loading) return;

    this.#loading = true;
    this.setAttribute('aria-busy', 'true');

    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        url: new URL(url, window.location.origin),
      });

      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.classList.contains('blog-grid__tag')) {
          active.blur();
        }
      });

      if (scroll) {
        this.#scrollToResults();
      }
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
    }
  }

  #scrollToResults() {
    const results = this.querySelector('.blog-grid__articles');
    const tools = this.querySelector('.blog-grid__tools');
    if (!(results instanceof HTMLElement)) return;

    const toolsHeight = tools instanceof HTMLElement ? tools.offsetHeight : 0;
    results.style.setProperty('--blog-grid-tools-height', `${toolsHeight}px`);

    results.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    });
    results.focus({ preventScroll: true });
  }
}

if (!customElements.get('blog-grid')) {
  customElements.define('blog-grid', BlogGrid);
}
